import { useState, useCallback } from "react";
import { FormData } from "@/pages/CompanyForm";
import { CompanyAccountState } from "@/types/company/company.types";

const initialAccountState: CompanyAccountState = {
  email: "",
  hasAssociatedAccount: false,
  isRegistered: false,
};

export const useCompanyForm = (initialData?: Partial<FormData>) => {
  const [formData, setFormData] = useState<FormData>({
    name: initialData?.name || "",
    nif: initialData?.nif || "",
    address: initialData?.address || "",
    calculationManager: initialData?.calculationManager || "",
    calculationManagerEmail: initialData?.calculationManagerEmail || "",
    associateNumber: initialData?.associateNumber || 0,
    activityType: initialData?.activityType || "",
    basinReference: initialData?.basinReference || "",
    weatherStationReference: initialData?.weatherStationReference || "",
    logoUrl: initialData?.logoUrl || "",
    accountState: initialData?.accountState || initialAccountState,
  });

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Handle nested accountState fields
    if (name.startsWith("accountState.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        accountState: {
          ...prev.accountState,
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  }, []);

  const handleNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? "" : !isNaN(parseInt(value, 10)) ? parseInt(value, 10) : prev[name as keyof FormData],
    }));
  }, []);

  const handleSelectChange = useCallback(
    (fieldName: string, value: string, weatherStations?: { id: string; name: string; basinId: string }[]) => {
      setFormData((prev) => {
        // If changing basin, clear the weather station if it doesn't belong to the new basin
        if (fieldName === "basinReference" && prev.weatherStationReference && weatherStations) {
          // Check if the current weather station belongs to the newly selected basin
          const stationExists = weatherStations.some(
            (station) => station.id === prev.weatherStationReference && station.basinId === value
          );

          return {
            ...prev,
            [fieldName]: value,
            weatherStationReference: stationExists ? prev.weatherStationReference : "",
          };
        }

        return {
          ...prev,
          [fieldName]: value,
        };
      });
    },
    []
  );

  const handleAddressChange = useCallback((address: string) => {
    setFormData((prev) => ({
      ...prev,
      address,
    }));
  }, []);

  const updateFormData = useCallback((newData: Partial<FormData>) => {
    setFormData((prev) => ({
      ...prev,
      ...newData,
    }));
  }, []);

  const updateAccountState = useCallback((newAccountState: Partial<CompanyAccountState>) => {
    setFormData((prev) => ({
      ...prev,
      accountState: {
        ...prev.accountState,
        ...newAccountState,
      },
    }));
  }, []);

  return {
    formData,
    setFormData,
    handleInputChange,
    handleNumberChange,
    handleSelectChange,
    handleAddressChange,
    updateFormData,
    updateAccountState,
  };
};
